import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Form, Formik, FormikProps } from 'formik'
import { useTranslation } from 'react-i18next'
import { NsKey } from 'enums/NsKey'
import { Button } from 'components/ui/Button'
import { GenericLoading, Sizes } from 'components/ui/Loader'
import { UploadIcon, XMarkIcon, CheckIcon } from 'components/icons/Icons'
import { ArrowLeft, Maximize2, Minimize2, ZoomIn, ZoomOut, RotateCw } from 'lucide-react'
import { InputFactory } from 'factories/InputFactory'
import { GenericMessage } from 'components/feedback/Message'
import {
  DocumentProcessDialogConfig,
  DocumentProcessState
} from './types'
import { InputsConfig } from 'types/form/inputsConfig'

type DocumentType = 'pdf' | 'image'

const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'image/bmp'
]

const getDocumentTypeFromMimeType = (mimeType?: string | null): DocumentType => {
  if (!mimeType) return 'pdf'
  if (IMAGE_MIME_TYPES.includes(mimeType)) return 'image'
  return 'pdf'
}

interface DocumentProcessDialogProps<
  TFormValues extends Record<string, unknown>,
  TExtracted = unknown
> {
  config: DocumentProcessDialogConfig<TFormValues, TExtracted>
  onClose: () => void
  onUploadFile: (file: File) => Promise<{ storedFileId: string; blobUrl: string; mimeType: string }>
}

export const DocumentProcessDialog = <
  TFormValues extends Record<string, unknown>,
  TExtracted = unknown
>({
  config,
  onClose,
  onUploadFile
}: DocumentProcessDialogProps<TFormValues, TExtracted>) => {
  const { t } = useTranslation([NsKey.Common])
  const formikRef = useRef<FormikProps<TFormValues>>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [state, setState] = useState<DocumentProcessState<TFormValues>>({
    step: 'upload',
    file: null,
    storedFileId: null,
    blobUrl: null,
    mimeType: null,
    isUploading: false,
    isExtracting: false,
    isSubmitting: false,
    extractionError: null,
    submitError: null,
    formValues: config.formConfig.initialValues
  })

  // Viewer state
  const [scale, setScale] = useState(100)
  const [rotation, setRotation] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)

  const documentType = getDocumentTypeFromMimeType(state.mimeType)

  // Build InputsConfig from fields for InputFactory
  const inputsConfig = useMemo(() => {
    const configMap: InputsConfig<string> = {}
    config.formConfig.fields.forEach(field => {
      configMap[field.name] = field as any
    })
    return configMap
  }, [config.formConfig.fields])

  // Viewer controls
  const handleZoomIn = useCallback(() => {
    setScale(prev => Math.min(prev + 25, 200))
  }, [])

  const handleZoomOut = useCallback(() => {
    setScale(prev => Math.max(prev - 25, 50))
  }, [])

  const handleRotate = useCallback(() => {
    setRotation(prev => (prev + 90) % 360)
  }, [])

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev)
  }, [])

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isFullscreen) {
          setIsFullscreen(false)
        } else {
          onClose()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isFullscreen, onClose])

  // Prevent body scroll in fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isFullscreen])

  const handleFileSelect = useCallback(async (file: File) => {
    // Validate file size
    if (config.maxFileSizeMB) {
      const maxSize = config.maxFileSizeMB * 1024 * 1024
      if (file.size > maxSize) {
        setState(prev => ({
          ...prev,
          extractionError: `Le fichier dépasse la taille maximale de ${config.maxFileSizeMB}MB`
        }))
        return
      }
    }

    setState(prev => ({
      ...prev,
      file,
      isUploading: true,
      extractionError: null
    }))

    try {
      // Upload the file
      const { storedFileId, blobUrl, mimeType } = await onUploadFile(file)

      setState(prev => ({
        ...prev,
        storedFileId,
        blobUrl,
        mimeType,
        isUploading: false,
        step: 'preview'
      }))

      // If extraction is configured, run it
      if (config.extraction) {
        setState(prev => ({ ...prev, isExtracting: true }))

        try {
          const result = await config.extraction.onExtract(storedFileId)

          if (result.success && result.data) {
            const mappedValues = config.extraction.mapper(result.data)
            setState(prev => ({
              ...prev,
              formValues: { ...prev.formValues, ...mappedValues },
              isExtracting: false,
              step: 'form'
            }))
          } else {
            setState(prev => ({
              ...prev,
              isExtracting: false,
              extractionError: result.errorMessage || 'Erreur lors de l\'extraction',
              step: 'form'
            }))
          }
        } catch (error) {
          console.error('Extraction error:', error)
          setState(prev => ({
            ...prev,
            isExtracting: false,
            extractionError: 'Erreur lors de l\'extraction des données',
            step: 'form'
          }))
        }
      } else {
        // No extraction, go directly to form
        setState(prev => ({ ...prev, step: 'form' }))
      }
    } catch (error) {
      console.error('Upload error:', error)
      setState(prev => ({
        ...prev,
        isUploading: false,
        extractionError: 'Erreur lors de l\'upload du fichier'
      }))
    }
  }, [config, onUploadFile])

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
    e.target.value = ''
  }, [handleFileSelect])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }, [handleFileSelect])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
  }, [])

  const handleSubmit = useCallback(async (values: TFormValues) => {
    if (!state.storedFileId) return

    setState(prev => ({ ...prev, isSubmitting: true, submitError: null }))

    try {
      const result = await config.submit.onSubmit(values, state.storedFileId)

      if (result.success) {
        config.onSuccess?.(result.data)
        onClose()
      } else {
        setState(prev => ({
          ...prev,
          isSubmitting: false,
          submitError: result.errorMessage || 'Erreur lors de la soumission'
        }))
      }
    } catch (error) {
      console.error('Submit error:', error)
      setState(prev => ({
        ...prev,
        isSubmitting: false,
        submitError: 'Erreur lors de la soumission'
      }))
    }
  }, [state.storedFileId, config, onClose])

  const handleBack = useCallback(() => {
    if (state.step === 'form') {
      setState(prev => ({ ...prev, step: 'preview' }))
    } else if (state.step === 'preview') {
      setState(prev => ({
        ...prev,
        step: 'upload',
        file: null,
        storedFileId: null,
        blobUrl: null,
        mimeType: null,
        extractionError: null
      }))
      // Reset viewer state
      setScale(100)
      setRotation(0)
    }
  }, [state.step])

  const handleContinueToForm = useCallback(() => {
    setState(prev => ({ ...prev, step: 'form' }))
  }, [])

  // Render document content (PDF or image)
  const renderDocumentContent = () => {
    if (!state.blobUrl) return null

    if (documentType === 'pdf') {
      return (
        <iframe
          key={`pdf-${scale}`}
          src={`${state.blobUrl}#toolbar=0&navpanes=0&zoom=${scale}`}
          className="bg-white shadow-lg w-full h-full"
          style={{
            border: 'none',
            transform: `rotate(${rotation}deg)`,
            transition: 'transform 0.3s ease'
          }}
          title={state.file?.name || 'Document'}
        />
      )
    }

    return (
      <img
        src={state.blobUrl}
        alt={state.file?.name || 'Document'}
        className="max-w-full max-h-full object-contain shadow-lg bg-white"
        style={{
          transform: `scale(${scale / 100}) rotate(${rotation}deg)`,
          transition: 'transform 0.3s ease'
        }}
      />
    )
  }

  // Viewer toolbar
  const renderViewerToolbar = () => (
    <div className="flex items-center gap-2">
      {/* Zoom controls */}
      <div className="flex items-center gap-1 px-2 py-1 bg-white rounded-lg border border-gray-200">
        <button
          onClick={handleZoomOut}
          disabled={scale <= 50}
          className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          title="Zoom arrière"
        >
          <ZoomOut className="w-4 h-4 text-gray-600" />
        </button>
        <span className="text-sm text-gray-600 min-w-[3rem] text-center">{scale}%</span>
        <button
          onClick={handleZoomIn}
          disabled={scale >= 200}
          className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          title="Zoom avant"
        >
          <ZoomIn className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {/* Rotate */}
      <button
        onClick={handleRotate}
        className="p-2 hover:bg-gray-100 rounded-lg border border-gray-200 bg-white"
        title="Rotation"
      >
        <RotateCw className="w-4 h-4 text-gray-600" />
      </button>

      {/* Fullscreen toggle */}
      <button
        onClick={toggleFullscreen}
        className="p-2 hover:bg-gray-100 rounded-lg border border-gray-200 bg-white"
        title={isFullscreen ? 'Réduire' : 'Plein écran'}
      >
        {isFullscreen ? (
          <Minimize2 className="w-4 h-4 text-gray-600" />
        ) : (
          <Maximize2 className="w-4 h-4 text-gray-600" />
        )}
      </button>
    </div>
  )

  // Document viewer section (used in preview and form steps)
  const renderDocumentViewer = (showToolbar = true) => (
    <div className="flex flex-col h-full">
      {showToolbar && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50 shrink-0">
          <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
            {state.file?.name || 'Document'}
          </span>
          {renderViewerToolbar()}
        </div>
      )}
      <div className="flex-1 relative bg-gray-100 overflow-auto">
        <div className="w-full h-full flex items-center justify-center p-4">
          {renderDocumentContent()}
        </div>
      </div>
    </div>
  )

  // Form section
  const renderForm = () => (
    <div className="flex flex-col h-full overflow-auto p-4">
      {state.extractionError && (
        <GenericMessage
          className="mb-4"
          type="warning"
          dismissible={false}
          message={state.extractionError}
        />
      )}

      <Formik
        innerRef={formikRef}
        initialValues={state.formValues}
        validationSchema={config.formConfig.validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values }) => (
          <Form className="space-y-4">
            <div className="grid grid-cols-6 gap-4">
              {config.formConfig.fields.map(field => (
                <div
                  key={field.name}
                  className={`col-span-${field.colSpan || 6}`}
                >
                  <InputFactory
                    config={inputsConfig}
                    fieldName={field.name}
                    state={values}
                  />
                </div>
              ))}
            </div>

            {state.submitError && (
              <GenericMessage
                className="mt-4"
                type="error"
                dismissible={false}
                message={state.submitError}
              />
            )}
          </Form>
        )}
      </Formik>
    </div>
  )

  const renderUploadStep = () => (
    <div className="flex flex-col items-center justify-center p-8 h-[60vh]">
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={config.acceptedFormats || '.pdf,.jpg,.jpeg,.png'}
        onChange={handleFileInputChange}
      />

      <div
        className="w-full max-w-md border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-primary hover:bg-gray-50 transition-colors"
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        {state.isUploading ? (
          <div className="flex flex-col items-center gap-4">
            <GenericLoading size={Sizes.lg} loading />
            <p className="text-gray-600">Upload en cours...</p>
          </div>
        ) : (
          <>
            <UploadIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-700 mb-2">
              {config.uploadLabel || 'Glissez-déposez votre fichier ici'}
            </p>
            <p className="text-sm text-gray-500 mb-4">ou cliquez pour sélectionner</p>
            <Button color="primary" onClick={(e: React.MouseEvent) => { e.stopPropagation(); fileInputRef.current?.click() }}>
              <UploadIcon className="w-4 h-4 mr-2" />
              Choisir un fichier
            </Button>
          </>
        )}
      </div>

      {state.extractionError && (
        <GenericMessage
          className="mt-4 max-w-md"
          type="error"
          dismissible={false}
          message={state.extractionError}
        />
      )}
    </div>
  )

  const renderPreviewStep = () => (
    <div className="flex flex-col h-[70vh]">
      {state.isExtracting ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <GenericLoading size={Sizes.lg} loading />
          <p className="text-gray-600">
            {config.extraction?.loadingMessage || 'Extraction des données en cours...'}
          </p>
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-hidden">
            {renderDocumentViewer()}
          </div>

          <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
            <Button outline onClick={handleBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <Button color="primary" onClick={handleContinueToForm}>
              Continuer
            </Button>
          </div>
        </>
      )}
    </div>
  )

  const renderFormStep = () => (
    <div className="flex flex-col h-[70vh]">
      <div className="flex gap-4 flex-1 overflow-hidden">
        {/* Document preview (left side) */}
        <div className="w-1/2 border-r border-gray-200 overflow-hidden">
          {renderDocumentViewer()}
        </div>

        {/* Form (right side) */}
        <div className="w-1/2 overflow-hidden">
          {renderForm()}
        </div>
      </div>

      <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
        <Button outline onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <Button
          color="primary"
          onClick={() => formikRef.current?.submitForm()}
          disabled={state.isSubmitting}
        >
          {state.isSubmitting ? (
            <>
              <GenericLoading size={Sizes.sm} isInline loading />
              <span className="ml-2">Validation...</span>
            </>
          ) : (
            <>
              <CheckIcon className="w-4 h-4 mr-2" />
              {config.submit.submitLabel || 'Valider'}
            </>
          )}
        </Button>
      </div>
    </div>
  )

  // Fullscreen content - shows document + form side by side
  const renderFullscreenContent = () => (
    <div className="fixed inset-0 z-[9999] flex flex-col bg-white">
      {/* Header with close button */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 shrink-0">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{config.title}</h2>
          {config.subtitle && (
            <p className="text-sm text-gray-500 mt-1">{config.subtitle}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {renderViewerToolbar()}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Document (left side - larger in fullscreen) */}
        <div className="w-2/3 border-r border-gray-200 bg-gray-100 overflow-auto">
          <div className="w-full h-full flex items-center justify-center p-4">
            {renderDocumentContent()}
          </div>
        </div>

        {/* Form (right side) */}
        <div className="w-1/3 flex flex-col">
          {renderForm()}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50 shrink-0">
        <Button outline onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <Button
          color="primary"
          onClick={() => formikRef.current?.submitForm()}
          disabled={state.isSubmitting}
        >
          {state.isSubmitting ? (
            <>
              <GenericLoading size={Sizes.sm} isInline loading />
              <span className="ml-2">Validation...</span>
            </>
          ) : (
            <>
              <CheckIcon className="w-4 h-4 mr-2" />
              {config.submit.submitLabel || 'Valider'}
            </>
          )}
        </Button>
      </div>
    </div>
  )

  // Render fullscreen via portal when in form step
  if (isFullscreen && state.step === 'form') {
    return (
      <>
        <div className="hidden" />
        {createPortal(renderFullscreenContent(), document.body)}
      </>
    )
  }

  return (
    <div className="flex flex-col bg-white rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{config.title}</h2>
          {config.subtitle && (
            <p className="text-sm text-gray-500 mt-1">{config.subtitle}</p>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <XMarkIcon className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Steps indicator */}
      <div className="flex items-center justify-center gap-2 py-3 border-b border-gray-100">
        <StepIndicator
          step={1}
          label="Upload"
          isActive={state.step === 'upload'}
          isCompleted={state.step !== 'upload'}
        />
        <div className="w-8 h-px bg-gray-300" />
        <StepIndicator
          step={2}
          label="Aperçu"
          isActive={state.step === 'preview'}
          isCompleted={state.step === 'form'}
        />
        <div className="w-8 h-px bg-gray-300" />
        <StepIndicator
          step={3}
          label="Formulaire"
          isActive={state.step === 'form'}
          isCompleted={false}
        />
      </div>

      {/* Content */}
      {state.step === 'upload' && renderUploadStep()}
      {state.step === 'preview' && renderPreviewStep()}
      {state.step === 'form' && renderFormStep()}
    </div>
  )
}

interface StepIndicatorProps {
  step: number
  label: string
  isActive: boolean
  isCompleted: boolean
}

const StepIndicator: FC<StepIndicatorProps> = ({ step, label, isActive, isCompleted }) => (
  <div className="flex items-center gap-2">
    <div
      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
        isCompleted
          ? 'bg-green-500 text-white'
          : isActive
            ? 'bg-primary text-white'
            : 'bg-gray-200 text-gray-500'
      }`}
    >
      {isCompleted ? <CheckIcon className="w-3 h-3" /> : step}
    </div>
    <span className={`text-sm ${isActive ? 'font-medium text-gray-900' : 'text-gray-500'}`}>
      {label}
    </span>
  </div>
)
